import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import FullCalendar, { DatesSetArg, EventContentArg } from '@fullcalendar/react';
import { DatePicker, message, Popover, Spin } from 'antd';
import { formatDistanceStrict } from 'date-fns';
import moment from 'moment';
import { Button, PageTitle } from '@unbooking/ui-modules';
import { useFacility } from '@common/hooks';
import { useRepository } from '@context';
import { Armoured, Calendar, Drive, MSN, Pax, PDF, WarningIcon } from '@assets/svg/icons';
import {
  CalendarResourceLabelContentProps,
  DriveEditableStatus,
  IPlannerDrives,
  IPlannerDrivesApi,
  IPlannerEvent,
  IPlannerResources,
  IPlannerVehiclesApi,
  ProtectionType,
} from '@common/interfaces';
import { Serializer } from '@common/utils';
import { Planner } from './components';
import './MobilityPlannerPage.scss';

const DATE_FORMAT_API = 'YYYY-MM-DD';

const MobilityPlannerNewPage: FC = () => {
  const { plannerRepository } = useRepository();
  const { t } = useTranslation();
  const {
    agencyName,
    facility: { agencyId, city, country, facilityId },
  } = useFacility();

  const { id: bookingId } = useParams<{ id: string }>();

  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(null);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const [drives, setDrives] = useState<IPlannerDrives[]>([]);
  const [events, setEvents] = useState<IPlannerEvent[]>([]);
  const [vehicles, setVehicles] = useState<IPlannerResources[]>([]);

  const calendarRef = useRef<FullCalendar | null>(null);

  useQuery(
    [`get-drives`, currentDate],
    () =>
      plannerRepository.getDrives(facilityId, {
        agencyId,
        date: moment(currentDate).format(DATE_FORMAT_API),
        isTemporary: 'false',
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!agencyId && !!currentDate,
      onSuccess: (data: IPlannerDrivesApi[]) => {
        setDrives(data.map(Serializer.formatDrives));
        setEvents(data.map(Serializer.formatDrivesToPlannerEvents));
      },
      onError: (error: any) => {
        if (error.message) {
          message.error(error.message || t('common.errorMessageDefault'));
        }
      },
    },
  );

  const { isLoading: isVehiclesLoading } = useQuery(
    [`get-vehicles`, currentDate],
    () =>
      plannerRepository.getVehicles(facilityId, {
        agencyId,
        date: moment(currentDate).format(DATE_FORMAT_API),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!agencyId && !!currentDate,
      onSuccess: (data: IPlannerVehiclesApi[]) => {
        setVehicles(data.map(Serializer.formatVehiclesToPlannerResources));
      },
      onError: (error: any) => {
        if (error.message) {
          message.error(error.message || t('common.errorMessageDefault'));
        }
      },
    },
  );

  const handleEventDoubleClick = async (): Promise<void> => {
    console.log('Edit Drive');
  };

  const handleViewChange = ({ startStr }: DatesSetArg): void => {
    if (currentDate && !moment(currentDate).isSame(startStr, 'day')) {
      setCurrentDate(moment(startStr));
    }
  };

  const renderEventContent = (eventContent: EventContentArg): JSX.Element => {
    const {
      event: {
        end,
        extendedProps: {
          driverAgency,
          driverEmail,
          driverFirstName,
          driverLastName,
          driverPhone,
          dropoffLocation,
          dropoffTown,
          editableState,
          isConflicting,
          passengerAgency,
          passengerEmail,
          passengerFirstName,
          passengerLastName,
          passengerPax,
          passengerPhone,
          passengerRemarks,
          pickupLocation,
          pickupTown,
        },
        start,
      },
    } = eventContent;

    const isEventShort = moment(end).diff(moment(start), 'hours') < 2;

    const eventPopoverContent = (
      <div className="fc-event-popover drive">
        {isConflicting && (
          <div className="drive-conflicting">
            <WarningIcon />
            <span>
              {t('common.recurring.conflictMessage1')} <br />{' '}
              {t('common.recurring.conflictMessage2')}
            </span>
          </div>
        )}

        <div className="drive-schedule">
          {t('planner.driveScheduledFor')} <b>{formatDistanceStrict(start as Date, end as Date)}</b>
        </div>

        <div className="drive-info">
          <div className="column">
            {!driverAgency ? (
              <section>
                <p className="title">{t('planner.noDriverInformationAvailable')}</p>
              </section>
            ) : (
              <section>
                <p className="title">{t('common.driver')}</p>
                <p>
                  <span className="label">{t('common.email')}:</span>
                  {driverEmail}
                </p>
                <p>
                  <span className="label">{t('common.name')}:</span>
                  {driverFirstName} {driverLastName}
                </p>
                <p>
                  <span className="label">{t('common.phoneNumber')}:</span>
                  {driverPhone}
                </p>
                <p>
                  <span className="label">{t('common.agency')}:</span>
                  {driverAgency}
                </p>
              </section>
            )}

            {!passengerPax ? (
              <section>
                <p className="title">{t('planner.noPassengers')}</p>
              </section>
            ) : (
              <section>
                <p className="title">{t('common.passenger')}</p>
                <p>
                  <span className="label">{t('common.name')}:</span>
                  {passengerFirstName} {passengerLastName}
                </p>
                {editableState !== DriveEditableStatus.Grey && (
                  <>
                    <p>
                      <span className="label">{t('common.email')}:</span>
                      {passengerEmail}
                    </p>

                    <p>
                      <span className="label">{t('common.phoneNumber')}:</span>
                      {passengerPhone}
                    </p>
                  </>
                )}
                <p>
                  <span className="label">{t('common.agency')}:</span>
                  {passengerAgency}
                </p>
                <p>
                  <span className="label">{t('common.remarks')}:</span>
                  {passengerRemarks}
                </p>
                <p>
                  <span className="label">{t('planner.numberOfPax')}:</span>
                  {passengerPax}
                </p>
              </section>
            )}
          </div>

          <div className="column">
            <section>
              <p className="title">{t('planner.pickUp')}</p>
              <p>
                <span className="label">{t('planner.town')}:</span>
                {pickupTown}
              </p>
              <p>
                <span className="label">{t('mobility.location')}:</span>
                {pickupLocation}
              </p>
              <p>
                <span className="label">{t('common.when')}:</span>
                {moment(start).format('DD/MM/YYYY - HH:mm')}
              </p>
            </section>
            <section>
              <p className="title">{t('planner.dropOff')}</p>
              <p>
                <span className="label">{t('planner.town')}:</span>
                {dropoffTown}
              </p>
              <p>
                <span className="label">{t('mobility.location')}:</span>
                {dropoffLocation}
              </p>
              <p>
                <span className="label">{t('common.when')}:</span>
                {moment(end).format('DD/MM/YYYY - HH:mm')}
              </p>
            </section>
          </div>
        </div>
      </div>
    );

    return (
      <Popover content={eventPopoverContent} placement="bottom">
        <div
          className="fc-event-content"
          onDoubleClick={() => handleEventDoubleClick()}
          data-testid="mobility-planner-event"
        >
          {isEventShort ? (
            <div className="icon drive">
              <Drive />
            </div>
          ) : (
            <>
              {passengerPax ? (
                <>
                  <span>
                    <Pax /> {passengerPax}
                  </span>
                  <span>
                    <MSN /> {passengerFirstName} {passengerLastName}
                  </span>
                </>
              ) : null}
              <span>
                <Drive /> {driverFirstName} {driverLastName}
              </span>
            </>
          )}
        </div>
      </Popover>
    );
  };

  const renderResourceContent = (content: CalendarResourceLabelContentProps): JSX.Element => {
    const {
      resource: { extendedProps, title },
    } = content;

    return (
      <div className="fc-resource-content">
        <div className="fc-resource-content-vehicle-image">
          <img src={extendedProps.image} alt={title} />
          {extendedProps.protection === ProtectionType.Armoured && (
            <div className="icon armoured">
              <Armoured />
            </div>
          )}
        </div>
        <div className="fc-resource-content-desc">
          {extendedProps.agencyLogo && (
            <img src={extendedProps.agencyLogo} alt={title} className="agency-logo" />
          )}
          <div>
            <h3>{extendedProps.plateNumber}</h3>
            <span>{title}</span>
          </div>
        </div>
      </div>
    );
  };

  const headerToolbarActions = useMemo(
    () => (
      <div className="header-toolbar-actions">
        <div className="filter-field">
          <input
            className="input-search"
            placeholder={t('mobility.filterByPassengerLastName')}
            type="text"
            value={searchKeyword}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setSearchKeyword(event.currentTarget.value);
            }}
          />
          {searchKeyword?.length ? (
            <Button
              className="btn-reset"
              text="&#x2715;"
              type="button"
              variant="primary"
              onClick={() => {
                setSearchKeyword('');
              }}
            />
          ) : null}
        </div>
        <Button className="btn btn-export" icon={<PDF />} text="Export PDF" variant="submit" />
      </div>
    ),
    [searchKeyword, t],
  );

  const headerToolbarCalendar = useMemo(
    () => (
      <div className="header-toolbar-calendar">
        <DatePicker
          suffixIcon={<Calendar />}
          onChange={(date: moment.Moment | null) => {
            if (date) calendarRef.current?.getApi().gotoDate(date.toDate());
          }}
        />
      </div>
    ),
    [],
  );

  useEffect(() => {
    console.log('drives', drives);
  }, [drives]);

  useEffect(() => {
    if (bookingId) {
      console.log('bookingId', bookingId);
    } else {
      setCurrentDate(moment());
    }
  }, [bookingId]);

  const activeVehicles = useMemo(
    () => vehicles.filter((vehicle: IPlannerResources) => !vehicle.isDisabled),
    [vehicles],
  );

  const visibleEvents = useMemo(
    () =>
      searchKeyword.length
        ? events.filter((item: IPlannerEvent) =>
            item.extendedProps.passengerLastName.includes(searchKeyword),
          )
        : events,
    [events, searchKeyword],
  );

  return (
    <section className="mobility-planner-page">
      <PageTitle title={`${city}, ${country} | ${agencyName}`} />

      <Spin size="large" spinning={isVehiclesLoading}>
        <Planner
          eventContent={renderEventContent}
          events={visibleEvents}
          headerToolbarActions={headerToolbarActions}
          headerToolbarCalendar={headerToolbarCalendar}
          ref={calendarRef}
          resourceContent={renderResourceContent}
          resources={activeVehicles}
          onViewChange={handleViewChange}
        />
      </Spin>
    </section>
  );
};

export default MobilityPlannerNewPage;
